/**
 * Patient page tabs selectable from `?tab=<value>` deep links.
 * `korrespondance` / `correspondence` are aliases for the Korrespondance tab (`medcom`);
 * `referral` is an alias for the Henvisning tab (`henvisning`);
 * `assignments` is an alias for the Tilknytninger tab (`tilknytninger`);
 * `labsvar` / `lab` / `laboratoriesvar` are aliases for the Labsvar tab (`lab_results`);
 * `sms` / `email` / `messages` / `beskeder` are aliases for Kommunikation (`communication`).
 */
export const PATIENT_URL_TABS = [
  'appointments',
  'treatment_courses',
  'relations',
  'journal',
  'lab_results',
  'communication',
  'punch_cards',
  'invoices',
  'activity',
  'questionnaires',
  'medcom',
  'henvisning',
  'tilknytninger',
  'documents',
  'consent',
  'tasks',
  'sagsakter',
  'sygeforsikring',
  'video_meetings',
  'patient_reports',
] as const;

export type PatientUrlTab = (typeof PATIENT_URL_TABS)[number];

const TAB_ALIASES: Readonly<Record<string, PatientUrlTab>> = {
  aftaler: 'appointments',
  behandlingsforloeb: 'treatment_courses',
  forloeb: 'treatment_courses',
  courses: 'treatment_courses',
  relationer: 'relations',
  labsvar: 'lab_results',
  lab: 'lab_results',
  laboratoriesvar: 'lab_results',
  korrespondance: 'medcom',
  correspondence: 'medcom',
  filer: 'documents',
  files: 'documents',
  referral: 'henvisning',
  fakturaer: 'invoices',
  klippekort: 'punch_cards',
  kommunikation: 'communication',
  samtykke: 'consent',
  opgaver: 'tasks',
  spoergeskemaer: 'questionnaires',
  aktivitet: 'activity',
  assignments: 'tilknytninger',
  sms: 'communication',
  email: 'communication',
  messages: 'communication',
  beskeder: 'communication',
  videomode: 'video_meetings',
  videomøde: 'video_meetings',
  video: 'video_meetings',
  rapporter: 'patient_reports',
  rapport: 'patient_reports',
};

/** The tab a `?tab=` value selects, or null when it names no deep-linkable tab. */
export function patientTabFromParam(value: string | null | undefined): PatientUrlTab | null {
  const key = value?.trim().toLowerCase();
  if (!key) return null;
  if ((PATIENT_URL_TABS as readonly string[]).includes(key)) return key as PatientUrlTab;
  return TAB_ALIASES[key] ?? null;
}

/**
 * A tab asked for in the URL. Its permission can resolve after the page renders (the
 * Korrespondance tab waits for the clinic's Communicate connection), so the page may
 * first fall back to another tab; `fallback` remembers which, so the requested tab is
 * restored once allowed unless the user has picked another tab meanwhile.
 */
export type PendingTab<T extends string> = { tab: T; fallback: T | null };

/** `pending` = not yet known (e.g. MedCom connection still loading). */
export type TabAccess = boolean | 'pending';

function isAllowed(access: TabAccess | undefined): boolean {
  return access === true;
}

function isPendingAccess(access: TabAccess | undefined): boolean {
  return access === 'pending';
}

export function resolvePatientTab<T extends string>(
  activeTab: T,
  permissions: Readonly<Record<T, TabAccess>>,
  pending: PendingTab<T> | null,
): { tab: T; pending: PendingTab<T> | null; denied: boolean } {
  if (
    pending &&
    isAllowed(permissions[pending.tab]) &&
    (activeTab === pending.tab || activeTab === pending.fallback)
  ) {
    return { tab: pending.tab, pending: null, denied: false };
  }

  if (pending && isPendingAccess(permissions[pending.tab])) {
    const first = (Object.keys(permissions) as T[]).find((tab) => isAllowed(permissions[tab]));
    if (!first) return { tab: activeTab, pending, denied: false };
    const keep = activeTab === pending.tab || activeTab === pending.fallback;
    if (!keep && isAllowed(permissions[activeTab])) {
      return { tab: activeTab, pending: null, denied: false };
    }
    return {
      tab: first,
      pending: keep ? { tab: pending.tab, fallback: first } : null,
      denied: false,
    };
  }

  if (
    pending &&
    !isAllowed(permissions[pending.tab]) &&
    !isPendingAccess(permissions[pending.tab]) &&
    (activeTab === pending.tab || activeTab === pending.fallback)
  ) {
    return { tab: pending.tab, pending: null, denied: true };
  }

  if (!isAllowed(permissions[activeTab])) {
    const first = (Object.keys(permissions) as T[]).find((tab) => isAllowed(permissions[tab]));
    if (!first) return { tab: activeTab, pending, denied: false };
    const keep = pending && (activeTab === pending.tab || activeTab === pending.fallback);
    return { tab: first, pending: keep ? { tab: pending.tab, fallback: first } : null, denied: false };
  }
  if (pending && activeTab !== pending.tab && activeTab !== pending.fallback) {
    return { tab: activeTab, pending: null, denied: false };
  }
  return { tab: activeTab, pending, denied: false };
}
